import { useState } from "react";
import { Check, Copy, Linkedin, Share2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { trackShare, type SharePlatform } from "@/lib/shareAnalytics";

interface SocialShareRowProps {
  url?: string;
  title?: string;
  text?: string;
  className?: string;
  label?: string;
  surface?: string;
}

/**
 * Inline row of social share buttons. Use for prominent "share this page"
 * placements (landing page, results, articles).
 */
export function SocialShareRow({
  url,
  title = "Glowie, your AI skincare bestie",
  text = "Found my new skincare bestie. Glowie roasts my shelf, scores my glow, and writes routines that actually work.",
  className,
  label = "Share",
  surface,
}: SocialShareRowProps) {
  const [copied, setCopied] = useState(false);
  const shareUrl =
    url ?? (typeof window !== "undefined" ? window.location.href : "https://meetglowie.com");
  const enc = encodeURIComponent;

  const buttons: Array<{
    name: string;
    platform: SharePlatform;
    href: string;
    bg: string;
    icon: React.ReactNode;
  }> = [
    {
      name: "Share on X",
      platform: "x",
      href: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(shareUrl)}`,
      bg: "bg-foreground text-background hover:opacity-90",
      icon: <span aria-hidden="true" className="font-bold text-sm">𝕏</span>,
    },
    {
      name: "Share on Facebook",
      platform: "facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`,
      bg: "bg-[#1877F2] text-white hover:opacity-90",
      icon: <span className="font-bold text-sm">f</span>,
    },
    {
      name: "Share on LinkedIn",
      platform: "linkedin",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`,
      bg: "bg-[#0A66C2] text-white hover:opacity-90",
      icon: <Linkedin className="h-4 w-4" />,
    },
  ];

  const open = (href: string, platform: SharePlatform) => {
    trackShare({ platform, surface, url: shareUrl });
    window.open(href, "_blank", "noopener,noreferrer,width=600,height=600");
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      trackShare({ platform: "copy", surface, url: shareUrl });
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const tryNative = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title, text, url: shareUrl });
        trackShare({ platform: "native", surface, url: shareUrl });
      } catch (e) {
        if ((e as DOMException)?.name !== "AbortError") console.error(e);
      }
    } else {
      copy();
    }
  };

  return (
    <div className={cn("flex items-center gap-2 flex-wrap", className)}>
      {label && (
        <span className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mr-1 inline-flex items-center gap-1.5">
          <Share2 className="h-3 w-3" /> {label}
        </span>
      )}
      {buttons.map((b) => (
        <button
          key={b.name}
          type="button"
          aria-label={b.name}
          onClick={() => open(b.href, b.platform)}
          className={cn(
            "h-9 w-9 rounded-full flex items-center justify-center shadow-soft transition-transform hover:-translate-y-0.5",
            b.bg,
          )}
        >
          {b.icon}
        </button>
      ))}
      <button
        type="button"
        aria-label="Copy link"
        onClick={copy}
        className="h-9 w-9 rounded-full flex items-center justify-center bg-card border border-border text-foreground shadow-soft transition-transform hover:-translate-y-0.5"
      >
        {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
      </button>
      <button
        type="button"
        aria-label="More share options"
        onClick={tryNative}
        className="sm:hidden h-9 w-9 rounded-full flex items-center justify-center bg-card border border-border text-foreground shadow-soft"
      >
        <Share2 className="h-4 w-4" />
      </button>
    </div>
  );
}

export default SocialShareRow;